/**
 * BUG-1960 — the one minor-unit rule for an operator-typed amount, and its copy
 * ("Amount must be in steps of ¥1", BL-2020, approved by Michael Lawrie
 * 2026-10-01), shared by the lot money writes and the schedule writes.
 */
import Decimal from 'decimal.js';

import {
  format,
  isSupportedCurrency,
  isWholeMinorUnits,
  minorUnitStep,
  Money,
  type MoneyWire,
  type ScheduleCurrencyOption,
  type SupportedCurrency,
} from '@auctogon/core/money';
import { z } from '@auctogon/core/zod';

/**
 * The refusal for an amount finer than its currency's minor unit, naming the
 * step in that currency: `$0.01`, `¥1`, `CA$0.01`.
 *
 * @param currency - A supported ISO 4217 code.
 * @returns The operator-facing message.
 */
export function minorUnitStepMessage(currency: SupportedCurrency): string {
  return `Amount must be in steps of ${format(minorUnitStep(currency))}`;
}

/**
 * Whether a typed amount has residue below its currency's minor unit, and if
 * so the message to put under its field.
 *
 * An unknown or unsupported currency judges nothing (BUG-1960 decision D2): the
 * step is a property of the currency, and guessing one would refuse — or pass —
 * an amount by the wrong rule (ADR 060 — Money Type). A server write always
 * resolves the record's currency first; only a client still loading it, or a
 * row stored in a currency nobody supports, reaches that branch.
 *
 * @param opts.amount - The amount in major units; a number is read through its
 *   shortest round-trip form, so no float digits are invented.
 * @param opts.currency - ISO 4217 code of the record, or null while unknown.
 * @returns The refusal message, or `null` when the amount fits (or can't be judged).
 */
export function amountStepRefusal(opts: {
  amount: Decimal.Value;
  currency: string | null | undefined;
}): string | null {
  if (!isSupportedCurrency(opts.currency)) return null;
  const money = Money.of(new Decimal(opts.amount), opts.currency);
  return isWholeMinorUnits(money) ? null : minorUnitStepMessage(opts.currency);
}

/**
 * A ceiling far above any real auction value, used only to reject abuse — a
 * crafted body carrying `1e300` / `Infinity` (which `JSON.stringify` silently
 * turns into `null` on the JSONB write, the corruption class BUG-897 sits
 * next to).
 */
const MAX_SCHEDULE_AMOUNT = 1_000_000_000;

/**
 * One money amount on a fee schedule — a buyer's-premium threshold, floor or
 * cap, or a seller-commission fee or tier boundary: finite, non-negative, under
 * the abuse ceiling, and a whole number of the schedule's minor units. Accepts
 * an explicit 0 (ADR 032 — Fee Cascade, ADR 045 — Commission Cascade).
 *
 * The schedule is bare JSONB numbers with no currency of its own, so the caller
 * that holds the record passes it.
 *
 * @param opts.currency - ISO 4217 code of the record the schedule belongs to; null while unknown.
 * @returns The field schema; a refused step lands on the field itself.
 */
export function scheduleAmountSchema(opts: ScheduleCurrencyOption) {
  return z
    .number()
    .finite()
    .nonnegative()
    .max(MAX_SCHEDULE_AMOUNT)
    .superRefine((value, ctx) => {
      // The sign and ceiling rules above already speak for these.
      if (value < 0 || value > MAX_SCHEDULE_AMOUNT) return;
      const message = amountStepRefusal({ amount: value, currency: opts.currency });
      if (message) ctx.addIssue({ code: 'custom', message });
    });
}

/** A plain decimal: optional minus, digits, optional fraction. No exponent, no commas. */
export const PLAIN_DECIMAL_RE = /^-?\d+(\.\d+)?$/;

/**
 * Whether a value is a `{ amount, currency }` pair of strings — the MoneyWire
 * shape, before any of its rules. Shared by the per-field refusals that word
 * their own copy (the card hold, BUG-1978; the minimum commission, BL-2315).
 *
 * @param value - Anything a request or form sent.
 * @returns `true` when it has the MoneyWire shape.
 */
export function isMoneyWireShape(value: unknown): value is MoneyWire {
  if (typeof value !== 'object' || value === null) return false;
  const { amount, currency } = value as { amount?: unknown; currency?: unknown };
  return typeof amount === 'string' && typeof currency === 'string';
}

/**
 * Whether a value is a `{ fraction }` RateWire carrying a plain decimal string —
 * the shape a schedule write body's rate must have before any of its rules.
 *
 * @param value - Anything a request or form sent.
 * @returns `true` when it is a decodable RateWire.
 */
export function isRateWireShape(value: unknown): value is { fraction: string } {
  if (typeof value !== 'object' || value === null) return false;
  const { fraction } = value as { fraction?: unknown };
  return typeof fraction === 'string' && PLAIN_DECIMAL_RE.test(fraction);
}

/**
 * One fee-schedule amount as a write body carries it — a `MoneyWire` in the
 * record's currency (BL-2492, ADR 060 — Money Type) — read as its value in major
 * units for the stored-shape schedule schema it is piped into.
 *
 * An amount named in another currency than the record's is refused, never
 * re-denominated. The minor-unit step runs HERE on the exact string, before the
 * number cast, so a residue a float would drop is still refused; the sign and
 * ceiling rules stay with {@link scheduleAmountSchema} downstream.
 *
 * @param opts.currency - ISO 4217 code of the record written; null while unknown.
 * @param message - The refusal for anything that is not such an amount.
 * @returns The field schema; its output is the amount as a number.
 */
export function scheduleMoneyWireAmount(opts: ScheduleCurrencyOption, message: string) {
  return z
    .custom<MoneyWire>(
      (value) =>
        isMoneyWireShape(value) &&
        PLAIN_DECIMAL_RE.test(value.amount) &&
        isSupportedCurrency(value.currency) &&
        (opts.currency == null || value.currency === opts.currency),
      { error: message },
    )
    .superRefine((wire, ctx) => {
      const step = amountStepRefusal({ amount: wire.amount, currency: wire.currency });
      if (step) ctx.addIssue({ code: 'custom', path: [], message: step });
    })
    .transform((wire) => new Decimal(wire.amount).toNumber());
}
